import { SystemIcon } from 'legos/system-icon';
import { Helper, Body, Bold } from 'legos/typography';
import { createElement, FC, ReactNode } from 'react';
import { useAlertTheme } from './alert.theme';
import {
  Container,
  IconContainer,
  MessageContainer,
  CtaContainer,
  CloseIcon,
  Wrapper,
  IconWrapper,
  OverrideTitle
} from './alert.ccm.css';
import { Anchor } from 'packages/react-nano-router';

const AlertTitle = OverrideTitle.as(Body);

export interface AlertProps {
  /**
   * Type of alert you want to render
   * @default status
   */
  type?: 'status' | 'important' | 'urgent';
  /**
   * Title of the alert
   */
  title: ReactNode;
  /**
   * Information message which will be displayed under title section
   */
  message?: ReactNode;
  /**
   * If specified, a CTA text will be shown at the bottom wrapped in an anchor
   */
  ctaAnchor?: Anchor;
  /**
   * If specified, a CTA text will be shown at the bottom
   */
  ctaText?: ReactNode;
  /**
   * An svg icon related to the alert
   */
  icon: ReactNode;
  /**
   * Box shadow
   */
  shadow?: boolean;
  /**
   * unique id of the alert
   */
  id?: string;
  /**
   * Callback fired when close icon is clicked
   */
  onClose?: () => void;
  /**
   * If specified, a CTA Comoponent will be shown at the bottom
   */
  ctaComponent?: ReactNode;
  alertType?: string;
  alertSubType?: string;
}

/**
 * Use `Alerts` to convey information to the user
 */
export const Alert: FC<AlertProps> = ({
  title,
  type = 'status',
  message,
  ctaAnchor,
  ctaText,
  icon,
  shadow = false,
  onClose,
  ctaComponent
}) => {
  const theme = useAlertTheme();
  const colorCode = theme[type === 'urgent' ? 'negative' : 'default'];

  return (
    <div data-testid="alert-box">
      <Container.div $backgroundColor={colorCode} $shadow={shadow}>
        <Wrapper.div>
          <IconContainer.div>{icon}</IconContainer.div>
          <MessageContainer.div>
            <AlertTitle>
              <Bold>{title}</Bold>
            </AlertTitle>
            {message && <Helper>{message}</Helper>}
            {ctaComponent && ctaComponent}
            {ctaAnchor && ctaText && (
              <CtaContainer.a {...ctaAnchor}>
                <Helper color="default">
                  <Bold>{ctaText}</Bold>
                </Helper>
                <IconWrapper.div>
                  <SystemIcon type="chevronRight" color="primary" size={2} />
                </IconWrapper.div>
              </CtaContainer.a>
            )}
          </MessageContainer.div>
        </Wrapper.div>
        {type === 'status' && (
          <CloseIcon.div data-testid="close-icon" onClick={onClose}>
            <SystemIcon type="closeX" size={1.2} />
          </CloseIcon.div>
        )}
      </Container.div>
    </div>
  );
};
